import React from 'react';
import { Link } from 'react-scroll';

const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden bg-white text-slate-900 pt-10 pb-12 md:pb-24">
      <div className="absolute inset-0 hero-mesh" />
      <div className="absolute inset-0 grid-dots opacity-50" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -left-40 -top-24 h-[520px] w-[520px] rounded-full bg-em/[0.12] blur-[130px] animate-blob" />
        <div className="absolute right-0 top-48 h-[380px] w-[380px] rounded-full bg-teal/[0.14] blur-[110px] animate-blob animation-delay-2000" />
        <div className="absolute left-[42%] bottom-0 h-[300px] w-[300px] rounded-full bg-em/[0.08] blur-[100px] animate-blob animation-delay-4000" />
      </div>

      <div className="absolute inset-x-0 top-0 h-px gradient-em opacity-70" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-14 items-center">

          {/* ── Left column ── */}
          <div className="lg:w-[56%] space-y-6">

            <div className="space-y-4">
              <h1 className="text-[1.85rem] sm:text-4xl md:text-[3.6rem] font-bold leading-tight md:leading-[1.08] tracking-tight text-slate-900">
                Executive strategy.<br />
                <span className="text-gradient">AI-engineered</span>
                {' '}
                <br className="hidden sm:block" />
                <span className="text-gradient">outcomes.</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-500 max-w-xl leading-relaxed">
                Forward-deployed leadership for the executive suite. We embed with your team — fractional CIO, COO, CTO, and CPO leadership that brings the right framework to each problem, from board-aligned strategy to AI agents and MCP-integrated products when the use case calls for it.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="services"
                smooth={true}
                duration={500}
                className="inline-flex items-center gap-2 btn-shimmer text-white px-7 py-3 rounded-full text-sm font-semibold shadow-glow-em hover:shadow-glow-em-lg transition-shadow cursor-pointer"
              >
                Explore our work
              </Link>
              <a
                href="/ai-assessment"
                className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-700 px-7 py-3 rounded-full text-sm font-semibold hover:border-em hover:text-em-dark transition-all shadow-sm"
              >
                Build a 90-Day Plan
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 divide-x divide-slate-100 border border-slate-100 rounded-2xl overflow-hidden shadow-card bg-white">
              <div className="p-3 sm:p-5">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">25+</p>
                <p className="text-[10px] text-em-dark uppercase tracking-widest mt-0.5 font-semibold">Years</p>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed hidden sm:block">C-Suite &amp; enterprise delivery</p>
              </div>
              <div className="p-3 sm:p-5">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">6-wk</p>
                <p className="text-[10px] text-em-dark uppercase tracking-widest mt-0.5 font-semibold">First Ship</p>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed hidden sm:block">Average to first AI build in prod</p>
              </div>
              <div className="p-3 sm:p-5">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">MCP</p>
                <p className="text-[10px] text-em-dark uppercase tracking-widest mt-0.5 font-semibold">AI-Native</p>
                <p className="text-xs text-slate-400 mt-1.5 leading-relaxed hidden sm:block">Agent &amp; API-first by default</p>
              </div>
            </div>
          </div>

          {/* ── Right — forward-deployed engineer card ── */}
          <div className="lg:w-[44%] w-full">
            <div className="relative animate-float">
              <div className="absolute inset-x-0 top-0 h-0.5 gradient-em rounded-t-3xl z-10" />

              <div className="relative rounded-3xl border border-slate-100 bg-white shadow-[0_12px_48px_rgba(16,185,129,0.10),_0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden">
                <div className="absolute inset-0 grid-dots opacity-30" />
                <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-em/[0.07] blur-2xl pointer-events-none" />
                <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-teal/[0.08] blur-2xl pointer-events-none" />

                <div className="relative p-5 sm:p-8">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2.5">
                      <div className="h-1 w-6 rounded-full bg-em" />
                      <div className="h-1 w-4 rounded-full bg-teal" />
                      <p className="text-[10px] text-em-deeper uppercase tracking-widest font-semibold ml-1">Forward Deployed</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-em animate-pulse" />
                      <span className="text-[10px] text-em-deeper uppercase tracking-widest font-semibold">Shipping outcomes</span>
                    </div>
                  </div>

                  {/* Deployment timeline visualization */}
                  <div className="rounded-2xl bg-slate-950 p-4 mb-5 font-mono overflow-x-auto">
                    <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-widest">Engagement · Field Deployment</p>
                    <div className="space-y-2">
                      {[
                        { label: 'Week 1', status: 'embedded · mapped 47 workflows', color: 'text-em' },
                        { label: 'Week 3', status: 'right-fit solution shipped to prod', color: 'text-teal' },
                        { label: 'Week 5', status: 'team trained · ownership handed off', color: 'text-em' },
                      ].map((step) => (
                        <div key={step.label} className="flex items-center gap-3">
                          <span className={`text-xs font-semibold w-20 flex-shrink-0 ${step.color}`}>{step.label}</span>
                          <span className="text-[11px] text-slate-400 leading-none">→</span>
                          <span className="text-[11px] text-slate-400">{step.status}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between">
                      <span className="text-[10px] text-slate-600">Embedded · shipping against real workflows</span>
                      <span className="text-[10px] text-em font-medium">● Deployed</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {[
                      { id: 'inside', text: 'Senior leadership working the problem beside you — not a deck handed over the wall.', accent: 'bg-em' },
                      { id: 'ship', text: 'The right framework for each use case — AI agents and MCP integrations among them — in production in weeks.', accent: 'bg-teal-dark' },
                      { id: 'own', text: 'Built to hand off — your team owns the outcome long after we step out.', accent: 'bg-em' },
                    ].map((point) => (
                      <div key={point.id} className="flex items-start gap-3">
                        <div className={`mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0 ${point.accent}`} />
                        <p className="text-sm text-slate-500 leading-relaxed">{point.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-5 -right-3 bg-white border border-em-border rounded-2xl shadow-card px-4 py-2.5 z-10">
                <p className="text-[10px] text-em-dark uppercase tracking-widest font-semibold">In production</p>
                <p className="text-sm font-bold text-slate-800 mt-0.5">Weeks, not quarters</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-em-light/40 to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
